import { faPaw } from '@fortawesome/pro-light-svg-icons';
import { faLayerGroup, faSearch } from '@fortawesome/pro-regular-svg-icons';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { status as appointmentStatus } from '@spike/appointment-model';
import type { AppointmentListItem } from '@spike/appointments-list-model';
import type { RoomDto, RoomRuleDto, RoomUnitDto } from '@spike/rooms-action';
import clsx from 'clsx';
import useApiClientWrapper from 'hooks/useApiClientWrapper';
import useAuth from 'hooks/useAuth';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import ClickAwayListener from 'react-click-away-listener';
import styles from './KanbanMoveDestinationMenu.module.scss';

interface UnitAvailability {
    text: string;
    warning: boolean;
}

interface DestinationUnit {
    id: number;
    name: string;
    active: boolean;
    capacity: number | null;
    occupancy: number;
    availability: UnitAvailability | null;
}

const nonConsumingStatuses = new Set([
    appointmentStatus.CANCELLED,
    appointmentStatus.NO_SHOW,
    appointmentStatus.DECLINED
]);

const occupancyFromAppointments = (appointments: Array<AppointmentListItem>) => {
    const counts = new Map<number, number>();
    appointments.forEach(appointment => {
        const unitId = appointment.room?.unit?.id;
        if (unitId == null || nonConsumingStatuses.has(appointment.status.id)) {
            return;
        }
        counts.set(unitId, (counts.get(unitId) ?? 0) + 1);
    });
    return counts;
};

interface DestinationRoom {
    id: number;
    name: string;
    roomUnits: Array<DestinationUnit>;
}

const ruleData = (rule?: RoomRuleDto) => {
    if (!rule) {
        return null;
    }
    try {
        return JSON.parse(rule.rule_data) as { quantity?: unknown; totalWeight?: unknown };
    } catch {
        return null;
    }
};

const numeric = (value: unknown) => (typeof value === 'number' ? value : undefined);

const isBySizeInvalid = (eligibility: RoomUnitDto['size_eligibility']) =>
    eligibility === 'mismatch' || eligibility === 'inconsistent';

const isBySizeMatch = (eligibility: RoomUnitDto['size_eligibility']) =>
    eligibility === 'empty_match' || eligibility === 'active_match';

const spotsLabel = (max: number, occupancy: number | undefined): UnitAvailability => {
    const occupied = typeof occupancy === 'number' ? occupancy : 0;
    const remaining = Math.max(max - occupied, 0);
    return { text: `${remaining}/${max}`, warning: remaining < 1 };
};

const unitCapacity = (
    rules: Array<RoomRuleDto>,
    unit: {
        active: boolean;
        available?: boolean;
        occupancy?: number;
        sizeEligibility?: RoomUnitDto['size_eligibility'];
    },
    hasBySize: boolean
): Pick<DestinationUnit, 'capacity' | 'occupancy' | 'availability'> => {
    const occupancy = typeof unit.occupancy === 'number' ? unit.occupancy : 0;

    if (!unit.active) {
        return { capacity: null, occupancy, availability: { text: 'Unavailable', warning: true } };
    }

    if (rules.some(rule => rule.rule_type_id === 4 || rule.rule_type_id === 3)) {
        return { capacity: null, occupancy, availability: { text: 'By Weight', warning: true } };
    }

    if (hasBySize) {
        if (isBySizeInvalid(unit.sizeEligibility)) {
            return { capacity: null, occupancy, availability: { text: 'By Size', warning: true } };
        }

        const max = numeric(ruleData(rules.find(rule => rule.rule_type_id === 2))?.quantity);
        if (max == null) {
            const available = isBySizeMatch(unit.sizeEligibility);
            return {
                capacity: null,
                occupancy,
                availability: { text: available ? 'Available' : 'Unavailable', warning: !available }
            };
        }

        return { capacity: max, occupancy, availability: null };
    }

    const max = numeric(
        ruleData(rules.find(rule => rule.rule_type_id === 1) ?? rules.find(rule => rule.rule_type_id === 2))?.quantity
    );
    if (max != null) {
        return { capacity: max, occupancy, availability: null };
    }

    const available = unit.available !== false;
    return {
        capacity: null,
        occupancy,
        availability: { text: available ? 'Available' : 'Unavailable', warning: !available }
    };
};

interface KanbanMoveDestinationMenuProps {
    appointments?: Array<AppointmentListItem>;
    anchor?: { top?: number; bottom?: number; right: number; maxHeight: number };
    onSelect: (roomUnitId: number | null, label: string) => void;
    onClose: () => void;
}

export const KanbanMoveDestinationMenu: FunctionComponent<KanbanMoveDestinationMenuProps> = props => {
    const auth = useAuth();
    const apiClientWrapper = useApiClientWrapper();
    const { organizationServicesInfo } = useOrganizationMarketplaces();
    const [searchText, setSearchText] = useState('');
    const [catalogRooms, setCatalogRooms] = useState<Array<DestinationRoom>>([]);

    const daycareAndBoardingRoomIds = useMemo(() => {
        const ids = new Set<number>();
        organizationServicesInfo.forEach(info => {
            [...(info.rooms?.daycare ?? []), ...(info.rooms?.boarding ?? [])].forEach(room => ids.add(room.id));
        });
        return ids;
    }, [organizationServicesInfo]);

    useEffect(() => {
        const marketplaceId = auth.marketplaceId;
        if (marketplaceId == null) {
            return;
        }

        let cancelled = false;
        const url = `/rooms?marketplace_id=${marketplaceId}&page=1&size=3210&marketplace_ids=${marketplaceId}`;
        apiClientWrapper.apiClient
            .get(url, apiClientWrapper.requestConfig)
            .then(response => {
                if (cancelled) {
                    return;
                }
                const rooms = ((response.data?.rooms ?? []) as Array<RoomDto>)
                    .filter(room => room.marketplace_id === marketplaceId && room.id != null)
                    .map(room => {
                        const rules = room.room_rules ?? [];
                        const sourceUnits = (room.room_units ?? []).filter(
                            unit => unit.id != null && unit.marketplace_id === marketplaceId
                        );
                        const hasBySize = sourceUnits.some(unit => unit.size_eligibility != null);
                        return {
                            id: room.id as number,
                            name: room.name,
                            roomUnits: sourceUnits.map(unit => ({
                                id: unit.id as number,
                                name: unit.name,
                                active: unit.active !== false,
                                ...unitCapacity(
                                    rules,
                                    {
                                        active: unit.active !== false,
                                        available: unit.available,
                                        occupancy: unit.occupancy,
                                        sizeEligibility: unit.size_eligibility
                                    },
                                    hasBySize
                                )
                            }))
                        };
                    })
                    .filter(room => room.roomUnits.length > 0)
                    .sort((a, b) => a.name.localeCompare(b.name));
                setCatalogRooms(rooms);
            })
            .catch(() => undefined);

        return () => {
            cancelled = true;
        };
        // The client wrapper is recreated every render; the catalog only depends on the logged-in location.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth.marketplaceId]);

    const usedByUnit = useMemo(() => occupancyFromAppointments(props.appointments ?? []), [props.appointments]);

    const currentLocationRooms = useMemo(() => {
        if (daycareAndBoardingRoomIds.size === 0) {
            return catalogRooms;
        }
        return catalogRooms.filter(room => daycareAndBoardingRoomIds.has(room.id));
    }, [catalogRooms, daycareAndBoardingRoomIds]);

    const query = searchText.trim().toLowerCase();
    const visibleRooms = currentLocationRooms
        .map(room => ({
            ...room,
            roomUnits: room.roomUnits.filter(unit => unit.id != null)
        }))
        .filter(room => room.roomUnits.length > 0)
        .filter(room => {
            if (query === '') {
                return true;
            }
            return (
                room.name.toLowerCase().includes(query) ||
                room.roomUnits.some(unit => unit.name.toLowerCase().includes(query))
            );
        });

    const showUnassigned = query === '' || 'unassigned'.includes(query);

    return (
        <ClickAwayListener onClickAway={props.onClose}>
            <div
                className={clsx(styles.menu, { [styles.menuAnchored]: props.anchor })}
                style={
                    props.anchor
                        ? {
                              top: props.anchor.top ?? 'auto',
                              bottom: props.anchor.bottom ?? 'auto',
                              right: props.anchor.right,
                              maxHeight: props.anchor.maxHeight
                          }
                        : undefined
                }
            >
                <span className={styles.title}>Move pets between rooms</span>
                <div className={styles.search}>
                    <FontAwesomeIcon icon={faSearch} />
                    <input
                        value={searchText}
                        placeholder="Unit name"
                        onChange={event => setSearchText(event.target.value)}
                    />
                </div>
                <div className={styles.list}>
                    {showUnassigned && (
                        <button
                            type="button"
                            className={styles.unit}
                            onClick={() => props.onSelect(null, 'Unassigned')}
                        >
                            Unassigned
                        </button>
                    )}
                    {visibleRooms.map(room => {
                        const units =
                            query !== '' && !room.name.toLowerCase().includes(query)
                                ? room.roomUnits.filter(unit => unit.name.toLowerCase().includes(query))
                                : room.roomUnits;

                        return (
                            <div
                                key={room.id}
                                className={styles.room}
                            >
                                <div className={styles.roomTitle}>
                                    <FontAwesomeIcon icon={faLayerGroup} />
                                    <span>{room.name}</span>
                                </div>
                                {units.map(unit => {
                                    const unavailable = unit.active === false;
                                    const availability =
                                        unit.capacity == null
                                            ? unit.availability
                                            : spotsLabel(
                                                  unit.capacity,
                                                  Math.max(unit.occupancy, usedByUnit.get(unit.id) ?? 0)
                                              );
                                    return (
                                        <button
                                            key={unit.id}
                                            type="button"
                                            className={clsx(styles.unit, { [styles.unavailable]: unavailable })}
                                            disabled={unavailable}
                                            onClick={() => props.onSelect(unit.id, `${room.name} (${unit.name})`)}
                                        >
                                            <span className={styles.unitName}>{unit.name}</span>
                                            {availability && (
                                                <span className={styles.availability}>
                                                    <FontAwesomeIcon
                                                        icon={availability.warning ? faExclamationTriangle : faPaw}
                                                    />
                                                    {availability.text}
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        );
                    })}
                    {!showUnassigned && visibleRooms.length === 0 && (
                        <span className={styles.empty}>Room not found.</span>
                    )}
                </div>
            </div>
        </ClickAwayListener>
    );
};

export default KanbanMoveDestinationMenu;
